import { ReactNode, SVGProps } from "react";
import { Typography } from "@kaltura/ds-react-components";
import { Stack } from "@kaltura/mediaspace-shared-styled";
import { translate, translateReact } from "@kaltura/mediaspace-shared-utils";
import styled from "@emotion/styled";
import { SiteLogo } from "../site-logo/SiteLogo";
import { isIOS, isSafari } from "react-device-detect";
import { BaseInstructionsDrawerProps, InstructionsDrawer } from "./InstructionsDrawer";
import { ButtonAnalyticsProvider } from "@kaltura/mediaspace-shared-hooks";

export interface ManualInstallationInstructionsDrawerProps extends BaseInstructionsDrawerProps {
    appIconUrl: string;
    isDesktop: boolean;
}

export const supportsManualInstallation = isIOS || isSafari;

/**
 * Bottom drawer with step-by-step iOS/Safari installation instructions.
 */
export const ManualInstallationInstructionsDrawer = ({
    appIconUrl,
    isDesktop,
    ...baseProps
}: ManualInstallationInstructionsDrawerProps) => (
    <ButtonAnalyticsProvider location={"Modal - iOS Install Guidance"}>
        <InstructionsDrawer
            {...baseProps}
            title={translate("Install the app")}
            logo={<SiteLogo src={appIconUrl} />}
            closeAnalyticsName={"Close ios guidance popup"}
        >
            <Step index={1}>
                {translateReact("Press %1 in browser menu", [<ShareIcon aria-label={translate("Share")} />])}
            </Step>
            <Step index={2}>
                {translateReact(
                    "Scroll down and press %1",
                    [<StyledTag>{isDesktop ? translate("Add to Dock") : translate("Add to Home Screen")}</StyledTag>]
                )}
            </Step>
            <Step index={3}>
                {isDesktop
                    ? translate("Look for app icon on your dock")
                    : translate("Look for app icon on your home screen")}
            </Step>
        </InstructionsDrawer>
    </ButtonAnalyticsProvider>
);

// Inline SVG for the iOS Share icon (ei:share-apple)
const StyledShareIcon = styled("svg")({
    display: "inline-block",
    verticalAlign: "middle",
});

const ShareIcon = (props: SVGProps<SVGSVGElement>) => (
    <StyledShareIcon width={16} height={16} viewBox={"0 0 16 16"} fill={"none"} {...props}>
        <g clipPath="url(#clip0_90843_20369)">
            <path
                d="M10.0416 3.54903L7.85931 1.36676L5.67704 3.54903L5.10059 2.97258L7.85931 0.213867L10.618 2.97258L10.0416 3.54903Z"
                fill="#3478F6"
            />
            <path d="M7.44757 0.790039H8.27106V9.43676H7.44757V0.790039Z" fill="#3478F6" />
            <path
                d="M11.9767 14.3781H3.74178C3.0418 14.3781 2.50653 13.8428 2.50653 13.1428V5.73134C2.50653 5.03137 3.0418 4.49609 3.74178 4.49609H6.62401V5.31959H3.74178C3.49473 5.31959 3.33003 5.48429 3.33003 5.73134V13.1428C3.33003 13.3899 3.49473 13.5546 3.74178 13.5546H11.9767C12.2238 13.5546 12.3885 13.3899 12.3885 13.1428V5.73134C12.3885 5.48429 12.2238 5.31959 11.9767 5.31959H9.0945V4.49609H11.9767C12.6767 4.49609 13.212 5.03137 13.212 5.73134V13.1428C13.212 13.8428 12.6767 14.3781 11.9767 14.3781Z"
                fill="#3478F6"
            />
        </g>
        <defs>
            <clipPath id="clip0_90843_20369">
                <rect width="16" height="16" fill="white" />
            </clipPath>
        </defs>
    </StyledShareIcon>
);

interface StepProps {
    index: number;
    children: ReactNode;
}
const Step = ({ index, children }: StepProps) => (
    <Stack direction={"row"} alignItems={"center"} gap={0.5}>
        <Typography variant={"body1Highlight"}>{index}.</Typography>
        <Typography variant={"body1"}>{children}</Typography>
    </Stack>
);

const StyledTag = styled("span")(({ theme }) => ({
    display: "inline-block",
    backgroundColor: theme.kaltura.palette.tone7,
    borderRadius: 4,
    padding: theme.spacing(0.5),
    fontFamily: "SF Pro Display, system-ui, sans-serif",
    fontSize: 14,
    fontWeight: 500,
}));
